import type { BillAction, BillStatus } from "./types";

export type BillOriginChamber = "House" | "Senate" | null | undefined;

const HOUSE_PASSAGE_PATTERNS = [
  /\bpassed\/agreed to in house\b/,
  /\bpassed house\b/,
  /\bhouse agreed to\b/,
  /\bpassed the house\b/,
  /\bagreed to in house\b/,
];
const SENATE_PASSAGE_PATTERNS = [
  /\bpassed\/agreed to in senate\b/,
  /\bpassed senate\b/,
  /\bsenate agreed to\b/,
  /\bpassed the senate\b/,
  /\bagreed to in senate\b/,
];
const PRESENTED_TO_PRESIDENT_PATTERNS = [/\bpresented to (the )?president\b/];
const INTRODUCTION_PATTERNS = [/introduced in (house|senate)/];
const COMMITTEE_PATTERNS = [/referred to (the )?committee/, /committee consideration and mark[- ]up session held/];
const REPORTED_PATTERNS = [/\breported\b/, /ordered to be reported/];
const RECORDED_VOTE_PATTERNS = [
  /recorded vote/,
  /\broll (?:no\.?|number)\b/,
  /\byea[- ]and[- ]nay\b/,
  /\bpassed\b.*\bvote\b/,
  /\bfailed\b.*\bvote\b/,
];
const LAW_PATTERNS = [
  /became public law/,
  /became private law/,
  /public law no[:.]/,
  /private law no[:.]/,
  /signed by president/,
  /signed into law/,
];

const billStatusLabels: Record<BillStatus, string> = {
  introduced: "Introduced",
  in_committee: "In committee",
  reported: "Reported",
  passed_one_chamber: "Passed one chamber",
  passed_congress: "Passed Congress",
  became_law: "Became law",
  failed: "Failed",
  vetoed: "Vetoed",
  unknown: "Unknown",
};

export function getBillPassedChamberLabel(originChamber: BillOriginChamber): string {
  if (originChamber === "House") return "Passed House";
  if (originChamber === "Senate") return "Passed Senate";
  return "Passed one chamber";
}

export function getBillStatusLabel(status: BillStatus, originChamber?: BillOriginChamber): string {
  if (status === "passed_one_chamber") {
    return getBillPassedChamberLabel(originChamber);
  }

  return billStatusLabels[status];
}

export function getBillAlertEventLabel(status: BillStatus, originChamber?: BillOriginChamber): string {
  switch (status) {
    case "reported":
      return "Reported by committee";
    case "passed_one_chamber":
      return getBillPassedChamberLabel(originChamber);
    case "passed_congress":
      return "Passed Congress";
    case "became_law":
      return "Became law";
    case "failed":
      return "Failed";
    case "vetoed":
      return "Vetoed";
    case "in_committee":
      return "In committee";
    case "introduced":
      return "Introduced";
    case "unknown":
    default:
      return "Status updated";
  }
}

export function getBillAlertNextStep(status: BillStatus, originChamber?: BillOriginChamber): string | null {
  switch (status) {
    case "reported":
      if (originChamber === "House") return "House floor";
      if (originChamber === "Senate") return "Senate floor";
      return "Origin chamber floor";
    case "passed_one_chamber":
      if (originChamber === "House") return "Senate";
      if (originChamber === "Senate") return "House";
      return "Opposite chamber";
    case "passed_congress":
      return "President";
    default:
      return null;
  }
}

export function isVoteLikeBillAlert(status: BillStatus, latestActionText: string | null | undefined): boolean {
  return status === "passed_one_chamber" || status === "passed_congress" || billActionLooksLikeRecordedVote(latestActionText);
}

export function isCommitteeStageBillStatus(status: BillStatus): boolean {
  return status === "in_committee" || status === "reported";
}

export function isIntroductionBillAction(action: BillAction): boolean {
  const actionType = normalizeBillText(action.actionType);
  return actionType.includes("intro") || matchesBillActionText(action, INTRODUCTION_PATTERNS);
}

export function isCommitteeBillAction(action: BillAction): boolean {
  const actionType = normalizeBillText(action.actionType);
  return actionType.includes("committee") || actionType.includes("referral") || matchesBillActionText(action, [...COMMITTEE_PATTERNS, ...REPORTED_PATTERNS]);
}

export function findEarliestBillActionDate(actions: BillAction[], matcher: (action: BillAction) => boolean): string | null {
  return earliestBillDate(
    actions.flatMap((action) => {
      if (!matcher(action)) return [];
      return [action.actionDate];
    }),
  );
}

export function matchesBillActionText(action: Pick<BillAction, "text">, patterns: RegExp[]): boolean {
  const text = normalizeBillText(action.text);
  return patterns.some((pattern) => pattern.test(text));
}

export function billActionIndicatesHousePassage(actionText: string | null | undefined): boolean {
  return HOUSE_PASSAGE_PATTERNS.some((pattern) => pattern.test(normalizeBillText(actionText)));
}

export function billActionIndicatesSenatePassage(actionText: string | null | undefined): boolean {
  return SENATE_PASSAGE_PATTERNS.some((pattern) => pattern.test(normalizeBillText(actionText)));
}

export function billActionIndicatesPresentationToPresident(actionText: string | null | undefined): boolean {
  return PRESENTED_TO_PRESIDENT_PATTERNS.some((pattern) => pattern.test(normalizeBillText(actionText)));
}

export function getOriginChamberPassageDate(originChamber: BillOriginChamber, housePassageDate: string | null, senatePassageDate: string | null): string | null {
  if (originChamber === "House") return housePassageDate;
  if (originChamber === "Senate") return senatePassageDate;
  return earliestBillDate([housePassageDate, senatePassageDate]);
}

export function getPassedCongressDate(housePassageDate: string | null, senatePassageDate: string | null): string | null {
  if (!housePassageDate || !senatePassageDate) return null;
  return laterBillDate(housePassageDate, senatePassageDate);
}

export function normalizeBillDate(value: string | null | undefined): string | null {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

export function earliestBillDate(values: Array<string | null | undefined>): string | null {
  const normalizedValues = values.flatMap((value) => {
    const normalized = normalizeBillDate(value);
    return normalized ? [normalized] : [];
  });

  if (normalizedValues.length === 0) return null;
  return normalizedValues.reduce((earliest, value) => (value < earliest ? value : earliest));
}

export function laterBillDate(left: string | null, right: string | null): string | null {
  const normalizedLeft = normalizeBillDate(left);
  const normalizedRight = normalizeBillDate(right);

  if (!normalizedLeft) return normalizedRight;
  if (!normalizedRight) return normalizedLeft;
  return normalizedLeft > normalizedRight ? normalizedLeft : normalizedRight;
}

export function billActionLooksLikeRecordedVote(actionText: string | null | undefined): boolean {
  const text = normalizeBillText(actionText);
  return RECORDED_VOTE_PATTERNS.some((pattern) => pattern.test(text));
}

export function billActionLooksLikeLaw(actionText: string | null | undefined): boolean {
  const text = normalizeBillText(actionText);
  return LAW_PATTERNS.some((pattern) => pattern.test(text));
}

function normalizeBillText(value: string | null | undefined): string {
  return (value ?? "").toLowerCase();
}
